import { makeStyles } from '@material-ui/styles';
import get from 'lodash.get';
import React, { useEffect, useRef, useState } from 'react';
import ClosePrompt from '../components/ClosePrompt';
import LazyLoading from '../components/LazyLoading/LazyLoading';
import NavigationProgress from '../components/NavigationProgress/NavigationProgress';
import { SUPPORT_EMAIL } from '../helpers/constants';
import {
  metricsDataFromState,
  submitMetric,
  subscribeMetrics,
  unsubscribeMetrics,
} from '../helpers/metrics';
import PropTypes, { consentPropType } from '../helpers/propTypes';
import { useSteps } from '../helpers/useSteps';
import { useIsLocked } from '../hooks/useCloseConfirmation';
import { useFullGlobalState } from '../hooks/useGlobalState';
import useId from '../hooks/useId';
import usePageAria from '../hooks/usePageAria';
import ErrorWrapper from './ErrorWrapper';
import Welcome from './Welcome';

const questionnaireImport = import('./Questionnaire');
const clinicFormImport = import('./ClinicForm');
const contactDetailsImport = import('./ContactDetails');
const registrationCompletedImport = import('./RegistrationCompleted');

const Questionnaire = React.lazy(() => questionnaireImport);
const ClinicForm = React.lazy(() => clinicFormImport);
const ContactDetails = React.lazy(() => contactDetailsImport);
const RegistrationCompleted = React.lazy(() => registrationCompletedImport);

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: 'white',
    margin: '0 auto',
    borderRadius: '6px',
    overflow: 'hidden',
    transition: 'all .3s ease-in',
    position: 'relative',
    width: 'calc(100% - 20px)',
    height: 'calc(100% - 20px)',
    zIndex: 9999,
    maxWidth: '930px',
    maxHeight: '720px',
    boxShadow: '0 0 24px 0 rgba(0, 0, 0, 0.36)',
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('sm')]: {
      maxWidth: 'unset',
      maxHeight: 'unset',
    },

    '@media print': {
      display: 'block',
      boxShadow: 'none',
      maxHeight: 'unset',
      width: '100%',
      height: 'auto',
    },
  },

  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',

    '@media print': {
      display: 'block',
      overflow: 'auto',
    },
  },
}));

const defaultContactFields = [
  {
    name: 'name',
    type: 'name',
  },
  {
    name: 'email',
    type: 'email',
    confirm: true,
  },
  {
    name: 'phone',
    type: 'phone',
  },
  {
    name: 'preferredContactHours',
    type: 'select',
    options: ['8-20', '8-12', '12-16', '16-20'],
    defaultValue: '8-20',
  },
];

const pageToComponent = {
  welcome: Welcome,
  questionnaire: Questionnaire,
  clinicForm: ClinicForm,
  contactDetails: ContactDetails,
  completed: RegistrationCompleted,
};

const getCustomPages = ({
  config: { customPages = {} } = {},
  sessionInfo: { study } = {},
  fullState: { clinicForm, contactDetails } = {},
}) => {
  const tags = {
    name: get(contactDetails, 'fieldValues.name.value', ''),
    email: get(contactDetails, 'fieldValues.email.value', ''),
    phone: get(contactDetails, 'fieldValues.phone.value', ''),
    siteName: get(clinicForm, 'selectedSite.name', ''),
    siteAddress: get(clinicForm, 'selectedSite.address', ''),
    inboundProxyNumber: get(clinicForm, 'selectedSite.inboundProxyNumber', ''),
    publicStudyName: get(study, 'publicStudyName', ''),
    supportEmail: `[${SUPPORT_EMAIL}](mailto:${SUPPORT_EMAIL})`,
  };

  const customPagesWithTags = { ...customPages };
  Object.keys(customPages).forEach((key) => {
    customPagesWithTags[key].tags = tags;
  });
  return customPagesWithTags;
};

function Modal({ onAction, sessionInfo, config, api, errorCode, onClose }) {
  const { defaultSiteId, questions, googleApiKey, consent } = sessionInfo;
  const { steps, contactForm, ...otherConfig } = config;

  const titleId = useId();
  const contentId = useId();
  const aria = usePageAria();
  const [sites, setSites] = useState();

  const {
    navItems,
    pageName,
    hasNextPage,
    toPreviousPage,
    toNextPage,
    nextIndex,
    previousIndex,
    stepIndex,
  } = useSteps({ steps, sites });

  const [showClosePrompt, setShowClosePrompt] = useState(false);
  const enableClosePrompt = useIsLocked();
  const [fullState, setGlobalState] = useFullGlobalState();

  const actions = useRef([]);
  const processActions = () => {
    while (actions.current.length) {
      const action = actions.current.shift();
      const metrics = metricsDataFromState({
        ...fullState,
        sessionInfo,
        pageName,
      });

      onAction(action, metrics);
    }
  };
  processActions();

  useEffect(() => {
    const callback = (action) => {
      actions.current.push(action);
      processActions();
    };
    subscribeMetrics(callback);

    return () => unsubscribeMetrics(callback);
  }, [fullState, sessionInfo, stepIndex]);

  useEffect(
    () => submitMetric('pageOpened'),
    [stepIndex, fullState[pageName].subPage || null],
  );

  useEffect(() => {
    if (defaultSiteId) {
      setGlobalState('clinicForm.selectedSite', defaultSiteId);
    }
  }, [defaultSiteId]);

  const customPages = getCustomPages({ config, sessionInfo, fullState });

  const navigate = ({ forward, sites: updatedSites }) => {
    if (forward && !hasNextPage) {
      onClose(customPages[pageName] && customPages[pageName].buttonAction);
    } else if (forward) {
      toNextPage(updatedSites);
    } else {
      toPreviousPage(updatedSites);
    }
  };

  const closeModal = (redirectUrl) => {
    const close = () => {
      if (showClosePrompt) {
        submitMetric('dropout');
        api.dropoutCandidate();
      } else {
        submitMetric('closed');
      }
      onClose(redirectUrl);
    };

    if (enableClosePrompt && !showClosePrompt) {
      setShowClosePrompt(redirectUrl || true);
    } else {
      close();
    }
  };

  const getPageProps = () => {
    const nextPageName = nextIndex < steps.length ? steps[nextIndex] : null;
    const prevPageName = previousIndex >= 0 ? steps[previousIndex] : null;

    return {
      ...otherConfig,
      api,
      questions,
      navigateModal: navigate,
      sites,
      setSites,
      googleApiKey,
      consent,
      closeModal,
      pageName,
      prevPageName,
      nextPageName,
      contentId,
      fields: contactForm || defaultContactFields,
      customPages,
      steps,
    };
  };

  const getStepContent = () => {
    const PageElement = pageToComponent[pageName];

    if (errorCode || !PageElement) {
      return <ErrorWrapper errorCode={errorCode} onButtonClick={closeModal} />;
    }

    return (
      <LazyLoading>
        <PageElement {...getPageProps()} />
      </LazyLoading>
    );
  };

  const getContent = () => {
    if (showClosePrompt) {
      return (
        <ClosePrompt
          customPage={customPages.leave}
          onCancel={() => setShowClosePrompt(false)}
          onClose={closeModal}
        />
      );
    }

    return getStepContent();
  };

  const classes = useStyles();

  // TODO: Role on the root div?
  return (
    <div aria-labelledby={titleId} className={classes.root} {...aria}>
      {!errorCode && (
        <NavigationProgress
          handleModalClose={() => closeModal()}
          isHidden={pageName === 'welcome'}
          navItems={navItems}
          progress={stepIndex}
          showCloseButton={!showClosePrompt}
          titleId={titleId}
        />
      )}
      <div
        aria-live="polite"
        className={classes.content}
        id={contentId}
        name="tb-sa-content"
      >
        {getContent()}
      </div>
    </div>
  );
}

Modal.propTypes = {
  api: PropTypes.shape({
    dropoutCandidate: PropTypes.func.isRequired,
    submitSite: PropTypes.func.isRequired,
  }).isRequired,
  config: PropTypes.shape({
    baseURI: PropTypes.string,
    contactForm: PropTypes.arrayOf(PropTypes.shape({})),
    country: PropTypes.string.isRequired,
    customPages: PropTypes.shape({}),
    locale: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.string).isRequired,
    studyId: PropTypes.string,
    userCoords: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    welcomeImage: PropTypes.string,
  }).isRequired,
  errorCode: PropTypes.string,
  onAction: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  sessionInfo: PropTypes.shape({
    consent: consentPropType.isRequired,
    defaultSiteId: PropTypes.string,
    googleApiKey: PropTypes.string.isRequired,
    questionnaire: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string,
        id: PropTypes.string,
        question: PropTypes.string,
        type: PropTypes.string,
      }),
    ),
    questions: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

Modal.defaultProps = {
  errorCode: null,
  sessionInfo: {
    consent: [],
    googleApiKey: '',
    questionnaire: [],
  },
};

export default Modal;

export function ErrorModal({ handleModalClose }) {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <ErrorWrapper errorCode="unknown" onButtonClick={handleModalClose} />
      </div>
    </div>
  );
}

ErrorModal.propTypes = {
  handleModalClose: PropTypes.func.isRequired,
};
